/* ET - Trustpilot Reviews (PDP-011).
   Everything is fetched live from the Trustpilot trustbox endpoint
   (slider template 54ad5defc6454f065c28af8b): TrustScore, review count,
   localized stars label, header stars AND the review cards themselves.
   - Desktop: two review columns auto-scroll vertically at different speeds
     (content duplicated once for a seamless loop); hovering a column pauses
     its scroll. Ported from the Cook & Pan trustpilot-section.
   - Mobile: scroll-snap slider with dots.
   - Every review card links to its review on Trustpilot. */
if (!customElements.get('et-trustpilot-reviews')) {
	const STAR_PATH =
		'M12.0001 16.175L15.6501 15.25L17.1751 19.95L12.0001 16.175ZM20.4001 10.1H13.9751L12.0001 4.05L10.0251 10.1H3.6001L8.8001 13.85L6.8251 19.9L12.0251 16.15L15.2251 13.85L20.4001 10.1Z'

	function starsSvg(rating, className) {
		let parts = ''
		for (let i = 0; i < 5; i++) {
			const boxX = i * 26
			const fill = Math.min(Math.max(rating - i, 0), 1)
			const greenW = fill * 24
			if (greenW > 0) parts += '<rect x="' + boxX + '" y="0" width="' + greenW + '" height="24" fill="#00B67A"/>'
			if (greenW < 24) parts += '<rect x="' + (boxX + greenW) + '" y="0" width="' + (24 - greenW) + '" height="24" fill="#DCDCE6"/>'
			parts += '<path transform="translate(' + boxX + ' 0)" d="' + STAR_PATH + '" fill="white"/>'
		}
		return (
			'<svg class="' + className + '" viewBox="0 0 128 24" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' + rating + '/5">' +
			parts +
			'</svg>'
		)
	}

	class ETTrustpilotReviews extends HTMLElement {
		connectedCallback() {
			this.load()
		}

		disconnectedCallback() {
			if (this.raf) cancelAnimationFrame(this.raf)
			if (this.observer) this.observer.disconnect()
			if (this.onResize) window.removeEventListener('resize', this.onResize)
		}

		load() {
			const businessUnit = this.dataset.businessUnit
			if (!businessUnit) {
				this.classList.add('is-empty')
				return
			}
			const params = new URLSearchParams({
				businessUnitId: businessUnit,
				locale: this.dataset.locale || 'nl-NL',
				reviewLanguages: this.dataset.reviewLanguages || 'nl',
				reviewsPerPage: this.dataset.reviewsCount || '12',
			})
			if (this.dataset.reviewStars) params.set('reviewStars', this.dataset.reviewStars)

			fetch('https://widget.trustpilot.com/trustbox-data/54ad5defc6454f065c28af8b?' + params.toString())
				.then((res) => (res.ok ? res.json() : null))
				.then((data) => {
					if (!data) throw new Error('no data')
					this.updateHeader(data)
					const reviews = Array.isArray(data.reviews) ? data.reviews.filter((r) => r && (r.text || r.title)) : []
					if (!reviews.length) {
						this.classList.add('is-empty')
						return
					}
					this.renderColumns(reviews)
					this.renderSlider(reviews)
					this.initColumns()
					this.initDots()
				})
				.catch(() => {
					this.classList.add('is-empty')
				})
		}

		updateHeader(data) {
			const unit = data.businessUnit || {}
			const displayLocale = document.documentElement.lang || 'nl'

			if (data.starsString) {
				this.querySelectorAll('[data-et-tpr-label]').forEach((el) => {
					el.textContent = data.starsString
				})
			}
			const headerStars = this.querySelector('.et-trustpilot-reviews__score-stars')
			if (headerStars && typeof unit.stars === 'number') {
				const holder = document.createElement('div')
				holder.innerHTML = starsSvg(unit.stars, 'et-trustpilot-reviews__score-stars')
				headerStars.replaceWith(holder.firstElementChild)
			}
			if (unit.trustScore) {
				let scoreText
				try {
					scoreText = Number(unit.trustScore).toLocaleString(displayLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
				} catch (e) {
					scoreText = String(unit.trustScore)
				}
				this.querySelectorAll('[data-et-tpr-score]').forEach((el) => {
					el.textContent = scoreText
				})
			}
			const total = unit.numberOfReviews && unit.numberOfReviews.total
			if (total) {
				let totalText
				try {
					totalText = Number(total).toLocaleString(displayLocale)
				} catch (e) {
					totalText = String(total)
				}
				this.querySelectorAll('[data-et-tpr-count]').forEach((el) => {
					el.textContent = totalText
				})
			}
		}

		buildCard(review) {
			const card = document.createElement(review.reviewUrl ? 'a' : 'div')
			card.className = 'et-trustpilot-reviews__review'
			if (review.reviewUrl) {
				card.href = review.reviewUrl
				card.target = '_blank'
				card.rel = 'noopener'
			}

			if (review.title) {
				const title = document.createElement('h3')
				title.className = 'et-trustpilot-reviews__review-title'
				title.textContent = review.title
				card.appendChild(title)
			}
			if (review.text) {
				const text = document.createElement('p')
				text.className = 'et-trustpilot-reviews__review-text'
				text.textContent = review.text
				card.appendChild(text)
			}

			const footer = document.createElement('div')
			footer.className = 'et-trustpilot-reviews__review-footer'
			if (review.verification && review.verification.isVerified) {
				const verified = document.createElement('span')
				verified.className = 'et-trustpilot-reviews__verified'
				verified.innerHTML =
					'<svg width="11" height="11" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.8125 3.9375L5.6875 10.0625L2.625 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
				footer.appendChild(verified)
			}
			const name = document.createElement('p')
			name.className = 'et-trustpilot-reviews__review-name'
			name.textContent = (review.consumer && review.consumer.displayName) || ''
			footer.appendChild(name)

			const stars = document.createElement('span')
			stars.className = 'et-trustpilot-reviews__review-stars-wrap'
			stars.innerHTML = starsSvg(review.stars || 5, 'et-trustpilot-reviews__review-stars')
			footer.appendChild(stars.firstElementChild)

			card.appendChild(footer)
			return card
		}

		renderColumns(reviews) {
			const columns = Array.from(this.querySelectorAll('[data-et-tpr-column]'))
			if (!columns.length) return
			const half = Math.ceil(reviews.length / 2)
			const groups = columns.length > 1 ? [reviews.slice(0, half), reviews.slice(half)] : [reviews]
			columns.forEach((el, i) => {
				const group = groups[i] || []
				group.forEach((review) => el.appendChild(this.buildCard(review)))
				if (!group.length) el.hidden = true
			})
		}

		renderSlider(reviews) {
			const slider = this.querySelector('[data-et-tpr-slider]')
			if (!slider) return
			reviews.forEach((review) => slider.appendChild(this.buildCard(review)))
		}

		initColumns() {
			const columns = Array.from(this.querySelectorAll('[data-et-tpr-column]')).filter((el) => !el.hidden)
			if (!columns.length) return

			this.columns = columns.map((el) => {
				el.innerHTML += el.innerHTML
				const column = {
					el,
					speed: parseFloat(el.dataset.scrollSpeed) || 0.5,
					offset: parseFloat(el.dataset.scrollOffset) || 0,
					half: 0,
					paused: false,
				}
				el.addEventListener('mouseenter', () => {
					column.paused = true
				})
				el.addEventListener('mouseleave', () => {
					column.paused = false
				})
				return column
			})

			const measure = () => {
				this.columns.forEach((column) => {
					column.half = column.el.scrollHeight / 2
				})
			}
			measure()
			this.onResize = measure
			window.addEventListener('resize', this.onResize)

			this.columns.forEach((column) => {
				column.el.style.transform = 'translateY(-' + column.offset + 'px)'
			})

			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

			let last = performance.now()
			let running = false

			const tick = (now) => {
				const delta = Math.min(now - last, 100) / 16.6667
				last = now
				this.columns.forEach((column) => {
					if (!column.half || column.paused) return
					column.offset = (column.offset + column.speed * delta) % column.half
					column.el.style.transform = 'translateY(-' + column.offset + 'px)'
				})
				this.raf = requestAnimationFrame(tick)
			}

			this.observer = new IntersectionObserver((entries) => {
				const visible = entries.some((entry) => entry.isIntersecting)
				if (visible && !running) {
					running = true
					last = performance.now()
					this.raf = requestAnimationFrame(tick)
				} else if (!visible && running) {
					running = false
					cancelAnimationFrame(this.raf)
				}
			})
			this.observer.observe(this)
		}

		initDots() {
			const slider = this.querySelector('[data-et-tpr-slider]')
			const dotsWrap = this.querySelector('[data-et-tpr-dots]')
			if (!slider || !dotsWrap) return

			const cards = Array.from(slider.children)
			if (cards.length < 2) return

			const dots = cards.map((card, index) => {
				const dot = document.createElement('button')
				dot.type = 'button'
				dot.className = 'et-trustpilot-reviews__dot'
				dot.setAttribute('aria-label', String(index + 1) + '/' + cards.length)
				dot.addEventListener('click', () => {
					slider.scrollTo({ left: card.offsetLeft - slider.offsetLeft, behavior: 'smooth' })
				})
				dotsWrap.appendChild(dot)
				return dot
			})

			const setActive = () => {
				const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 1
				const index = Math.max(0, Math.min(cards.length - 1, Math.round(slider.scrollLeft / step)))
				dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index))
			}
			setActive()
			slider.addEventListener('scroll', setActive, { passive: true })
		}
	}

	customElements.define('et-trustpilot-reviews', ETTrustpilotReviews)
}
